import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaPlus, FaSitemap, FaTrash } from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import { Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage } from "./workApi";
import "../dm/dm.css";
import "./work.css";

function Tree({ members, parentId, depth = 0 }) {
  const children = members.filter((m) => (m.Reports_To_User_Id || null) === parentId);
  if (!children.length || depth > 12) return null;
  return (
    <ul className="wk-tree">
      {children.map((m) => (
        <li key={m.User_Id}>
          <div className="wk-tree__node">
            <Avatar name={m.Name} size={26} />
            <strong>{m.Name}</strong>
            <span className="wk-muted">
              {[m.Designation, m.Department, m.Primary_Area].filter(Boolean).join(" · ")}
            </span>
          </div>
          <Tree members={members} parentId={m.User_Id} depth={depth + 1} />
        </li>
      ))}
    </ul>
  );
}

/** Reporting hierarchy: who reports to whom, departments and main work areas. */
function OrgSetup({ api }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [tree, setTree] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [error, setError] = useState("");
  const [view, setView] = useState("tree");

  const load = useCallback(async () => {
    try {
      const [t, d, a] = await Promise.all([api.orgTree(), api.departments(), api.areas()]);
      setTree(t);
      setDepartments(d || []);
      setAreas(a || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load your team"));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const members = useMemo(() => tree?.members || [], [tree]);
  const knownIds = useMemo(() => new Set(members.map((m) => m.User_Id)), [members]);
  const roots = members.filter((m) => !m.Reports_To_User_Id || !knownIds.has(m.Reports_To_User_Id));

  const update = async (userId, body) => {
    try {
      const row = await api.updateOrgMember(userId, body);
      setTree((t) => ({ ...t, members: t.members.map((m) => (m.User_Id === userId ? row : m)) }));
      notify.success("Saved");
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  const addDept = async (e) => {
    e.preventDefault();
    if (!newDept.trim()) return;
    try {
      await api.createDepartment({ Department_Name: newDept.trim() });
      setNewDept("");
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  const removeDept = async (d) => {
    if (!(await confirm({ title: "Remove department", message: `Remove "${d.Department_Name}"? People in it keep working as before.` }))) return;
    try {
      await api.deleteDepartment(d.Department_Id);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div className="dmp wk">
      <Link to={api.link("work/team")} className="dmp-back-link">
        <FaArrowLeft aria-hidden="true" /> Team board
      </Link>
      <PageHead title="Reporting Hierarchy">
        <div className="wk-seg" role="group" aria-label="View">
          <button type="button" aria-pressed={view === "tree"} onClick={() => setView("tree")}>
            Org chart
          </button>
          <button type="button" aria-pressed={view === "edit"} onClick={() => setView("edit")}>
            Set managers
          </button>
        </div>
      </PageHead>
      {error ? <div className="dmp-error">{error}</div> : null}
      {!tree ? (
        <div className="dmp-card wk-muted">Loading…</div>
      ) : view === "tree" ? (
        <div className="dmp-card">
          {members.length ? (
            <ul className="wk-tree">
              {roots.map((m) => (
                <li key={m.User_Id}>
                  <div className="wk-tree__node">
                    <Avatar name={m.Name} size={28} />
                    <strong>{m.Name}</strong>
                    <span className="wk-muted">
                      {[m.Designation, m.Department, m.Primary_Area].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <Tree members={members} parentId={m.User_Id} />
                </li>
              ))}
            </ul>
          ) : (
            <Empty icon={FaSitemap} title="No team members yet" />
          )}
        </div>
      ) : (
        <div className="wk-grid-2">
          <div className="dmp-card" style={{ overflowX: "auto" }}>
            <h3 className="dmp-card__title">People</h3>
            {!tree.can_edit ? <p className="wk-muted">Only an admin or the company owner can change everyone; you can change people in your team.</p> : null}
            <table className="wk-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Reports to</th>
                  <th>Department</th>
                  <th>Main area</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const editable = tree.can_edit || (m.In_My_Team && m.User_Id !== tree.me);
                  return (
                    <tr key={m.User_Id}>
                      <td>
                        <strong>{m.Name}</strong>
                        <div className="wk-muted" style={{ fontSize: 12 }}>
                          {m.Designation || m.Email}
                        </div>
                      </td>
                      <td>
                        <select
                          aria-label={`${m.Name} reports to`}
                          value={m.Reports_To_User_Id || ""}
                          disabled={!editable}
                          onChange={(e) => update(m.User_Id, { Reports_To_User_Id: e.target.value ? Number(e.target.value) : null })}
                        >
                          <option value="">— Top level —</option>
                          {members
                            .filter((x) => x.User_Id !== m.User_Id)
                            .map((x) => (
                              <option key={x.User_Id} value={x.User_Id}>
                                {x.Name}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <select
                          aria-label={`${m.Name} department`}
                          value={m.Department_Id || ""}
                          disabled={!editable}
                          onChange={(e) => update(m.User_Id, { Department_Id: e.target.value ? Number(e.target.value) : null })}
                        >
                          <option value="">—</option>
                          {departments.map((d) => (
                            <option key={d.Department_Id} value={d.Department_Id}>
                              {d.Department_Name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          aria-label={`${m.Name} main area`}
                          value={m.Primary_Area_Id || ""}
                          disabled={!editable}
                          onChange={(e) => update(m.User_Id, { Primary_Area_Id: e.target.value ? Number(e.target.value) : null })}
                        >
                          <option value="">—</option>
                          {areas.map((a) => (
                            <option key={a.Area_Id} value={a.Area_Id}>
                              {a.Area_Name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="dmp-card">
            <h3 className="dmp-card__title">Departments</h3>
            <ul className="wk-timeline">
              {departments.map((d) => (
                <li key={d.Department_Id}>
                  <span style={{ flex: 1 }}>{d.Department_Name}</span>
                  {tree.can_edit ? (
                    <button type="button" className="dmp-link" onClick={() => removeDept(d)} aria-label={`Remove ${d.Department_Name}`}>
                      <FaTrash aria-hidden="true" />
                    </button>
                  ) : null}
                </li>
              ))}
              {!departments.length ? <li className="wk-muted">No departments yet.</li> : null}
            </ul>
            {tree.can_edit ? (
              <form className="wk-quick-add" onSubmit={addDept}>
                <input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="New department" aria-label="New department" maxLength={100} />
                <button type="submit" className="dmp-btn dmp-btn--sm" disabled={!newDept.trim()}>
                  <FaPlus aria-hidden="true" /> Add
                </button>
              </form>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrgSetup;
